/**
 * Popularité des SKILLCARDS (migration 0283) — PUR, testé.
 *
 * Une note de 1 à 5 étoiles par compte et par carte (RPC `rate_card`) ; la
 * moyenne et le nombre de votes sont publics (`card_rating_stats`). À ne pas
 * confondre avec les étoiles de RARETÉ (★ dorées) : la note s'affiche avec
 * son propre pictogramme et toujours accompagnée du nombre d'avis.
 */

export const CARD_RATING_MAX = 5;

export interface CardRatingSummary {
  /** Moyenne (2 décimales) ou null sans aucun vote. */
  avg: number | null;
  count: number;
}

export function isValidCardStars(stars: unknown): stars is 1 | 2 | 3 | 4 | 5 {
  return Number.isInteger(stars) && (stars as number) >= 1 && (stars as number) <= CARD_RATING_MAX;
}

/** « 4,3 » (1 décimale, virgule française) ou null sans vote. */
export function formatCardRating(avg: number | null | undefined): string | null {
  if (avg == null || !Number.isFinite(avg)) return null;
  return (Math.round(avg * 10) / 10).toLocaleString('fr-FR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

/**
 * La note se demande comme une UTILITÉ (« Cette carte t'a-t-elle été utile ? ») :
 * un libellé par niveau, de 1 à 5.
 */
export const CARD_USEFULNESS_LABELS = [
  'Pas utile',
  'Peu utile',
  'Utile',
  'Très utile',
  'Indispensable',
] as const;

/** Libellé d'utilité d'une note 1..5 (null hors bornes). */
export function cardUsefulnessLabel(stars: number | null | undefined): string | null {
  return isValidCardStars(stars) ? CARD_USEFULNESS_LABELS[stars - 1]! : null;
}

/** « 12 avis », « 1 avis », « Aucun avis ». */
export function formatCardRatingCount(count: number): string {
  if (count <= 0) return 'Aucun avis';
  return `${count} avis`;
}

/**
 * Score de tri « populaires » : moyenne bayésienne (prior 3,5 sur 3 votes
 * fictifs) pour qu'une carte à 1 vote de 5 ne passe pas devant une carte à
 * 40 votes de 4,6. Sans vote : 0 (en fin de liste).
 */
export function cardPopularityScore(rating: CardRatingSummary | null | undefined): number {
  if (!rating || !rating.count || rating.avg == null) return 0;
  const PRIOR = 3.5;
  const WEIGHT = 3;
  return (rating.avg * rating.count + PRIOR * WEIGHT) / (rating.count + WEIGHT);
}

/** Normalise la valeur brute renvoyée par le RPC / la table (numeric → string). */
export function toCardRatingSummary(raw: unknown): CardRatingSummary {
  const r = (raw ?? {}) as {
    avg?: unknown;
    count?: unknown;
    rating_avg?: unknown;
    rating_count?: unknown;
  };
  const avgRaw = r.avg ?? r.rating_avg;
  const countRaw = r.count ?? r.rating_count;
  const avg = avgRaw == null ? null : Number(avgRaw);
  const count = countRaw == null ? 0 : Number(countRaw);
  return {
    avg: avg != null && Number.isFinite(avg) ? avg : null,
    count: Number.isFinite(count) ? count : 0,
  };
}
